import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { useMemo, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast, type RoleState } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { useStreamView } from "../../state/stream-view.tsx";
import type { AlertView } from "../../lib/stream/view-state.ts";
import { useAlertHistory, type AlertsWindow } from "./use-alerts.ts";
import { AlertList } from "./AlertList.tsx";
import { RulesEditor } from "./RulesEditor.tsx";
import { Silences } from "./Silences.tsx";
import { Channels } from "./Channels.tsx";
import { readErrorMessage, severityLabel, type Alert, type AlertPage } from "./vocab.ts";
import {
  DashboardGrid,
  KpiCard,
  AlertsPanel,
  type AlertsPanelItem,
} from "../../components/observability/index.ts";
import "./alerts.css";

/**
 * The Alerts screen (task 11.13; design 11 operator-API Alerts row, 12.2 Alerts row, 12.3/12.4
 * principles). The FIRST screen with WRITE operations, so role gating and the server-enforced
 * invariants are front and centre.
 *
 * Composition (reads are role `viewer`, writes role `operator`):
 * - FIRING: the currently firing/pending alerts, PREFERRING the live stream (`useStreamView().view
 *   .alerts`) for state freshness, overlaid on the REST alert bodies so the operator sees the full
 *   rule/severity/value with the latest state. Firing alerts are prominent (icon+text; never green).
 * - HISTORY: `GET /alerts` over the shared time-range window (pausing freezes it). Each alert is
 *   ackable (`POST /alerts/{id}/ack`, operator) — the ack control is disabled-with-a-reason for a
 *   viewer / unknown role and the server's 403 is still surfaced.
 * - RULES: list/create/edit (`GET/POST/PUT /alerts/rules`, operator writes).
 * - SILENCES: list/create/change/remove (`GET/POST/PUT/DELETE /alerts/silences`, operator writes),
 *   with the reason + bounded-duration invariant enforced client-side AND the server 400/422 handled.
 * - CHANNELS: list/create (`GET/POST /alerts/channels`, operator writes); the write-only signing
 *   secret is never echoed.
 *
 * The alert HISTORY read drives the scaffold's top-level state (loading / error / empty / restricted
 * / ready). The rules / silences / channels regions render their own loading/empty/error INSIDE the
 * ready screen, so a slow or empty secondary read never blanks the whole page — matching the
 * Identity screen's secondary-region pattern.
 */
export function AlertsScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();

  const canRead = roleAtLeast(role, "viewer");
  const { query, window } = useAlertHistory({ enabled: canRead });

  // The live view's alerts (keyed by rule id). Prefer the stream's state for freshness.
  const { view } = useStreamView();

  const history = useMemo<readonly Alert[]>(() => query.data?.items ?? [], [query.data]);

  const firing = useMemo<readonly Alert[]>(
    () => deriveFiring(history, view.alerts),
    [history, view.alerts],
  );

  const state = resolveState(role, query, history.length);

  return (
    <ScreenScaffold
      title="Alerts"
      subtitle="Firing alerts and history with acknowledgement, the rules editor, silences with a required reason and bounded duration, and signed-webhook channels. Reads need the viewer role; acknowledgements and rule, silence and channel changes need the operator role. The server enforces regardless."
      state={state}
    >
      {query.data !== undefined && (
        <AlertsBody role={role} live={live} window={window} page={query.data} firing={firing} history={history} />
      )}
    </ScreenScaffold>
  );
}

/**
 * Build the firing list, preferring the live stream's state. For each alert the REST page returned,
 * if the live stream carries that rule's state, use the live state (it is fresher); keep the REST
 * body for the full rule/severity/value. An alert whose current state (live-overridden) is firing or
 * pending is shown; a resolved one drops out of the firing list (it stays in history).
 */
function deriveFiring(
  history: readonly Alert[],
  liveAlerts: ReadonlyMap<string, AlertView>,
): readonly Alert[] {
  const out: Alert[] = [];
  for (const alert of history) {
    const live = liveAlerts.get(alert.rule_id);
    const state = live?.state ?? alert.state;
    if (state === "firing" || state === "pending") {
      out.push(state === alert.state ? alert : { ...alert, state });
    }
  }
  // Firing before pending, then most recent first.
  return out.sort((a, b) => rank(a) - rank(b) || b.since - a.since);
}

function rank(a: Alert): number {
  return a.state === "firing" ? 0 : 1;
}

/** Decide the scaffold state from the role and the alert-history query. */
function resolveState(
  role: RoleState,
  query: ReturnType<typeof useAlertHistory>["query"],
  historyCount: number,
): ScreenState {
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the alerts view is hidden until it is. The server enforces access regardless."
          : "The alerts view needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    if (historyCount === 0) {
      return {
        kind: "empty",
        cause:
          "No alerts in this window. Widen the time range, or create a rule so alerts can fire. Rules, silences and channels are still managed below.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: readErrorMessage(query.error) };
  }

  return { kind: "loading" };
}

function AlertsBody(props: {
  role: RoleState;
  live: boolean;
  window: AlertsWindow;
  page: AlertPage;
  firing: readonly Alert[];
  history: readonly Alert[];
}): ReactNode {
  const kpis = alertKpis(props.firing, props.history);
  const panelItems = firingPanelItems(props.firing);

  return (
    <div className="alr" data-live={props.live}>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="alr__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(props.window.fromMs)}>
          {formatTimeOfDay(props.window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(props.window.toMs)}>
          {formatTimeOfDay(props.window.toMs)}
        </time>
        {" · "}
        {props.firing.length === 0
          ? "nothing firing"
          : `${props.firing.length} firing or pending`}
      </p>
      </div>

      {/* At-a-glance dashboard header built from the SAME firing/history reads the regions below
          use — a KpiCard summary row and the kit AlertsPanel for the firing list. The detailed,
          ackable tables (Firing/History) remain unchanged below; this is a presentational overview,
          not a new data read (observability-ui task 3.1). */}
      <DashboardGrid
        label="Alerts summary"
        className="alr-dashboard"
        kpis={kpis.map((kpi) => (
          <KpiCard key={kpi.key} {...kpi.props} />
        ))}
      >
        <AlertsPanel heading="Firing now" items={panelItems} total={props.firing.length} />
      </DashboardGrid>

      <section className="alr-region" aria-label="Firing alerts">
        <h2 className="alr-region__title">Firing</h2>
        <p className="alr-region__note alr-faint">
          Current firing and pending alerts, refreshed from the live stream. Acknowledge one to record
          why it is being handled.
        </p>
        <AlertList
          alerts={props.firing}
          role={props.role}
          caption="Currently firing or pending alerts, firing first."
          emptyMessage="Nothing is firing or pending right now."
        />
      </section>

      <section id="alr-history" className="alr-region" aria-label="Alert history">
        <h2 className="alr-region__title">History</h2>
        <AlertList
          alerts={props.history}
          role={props.role}
          caption="Alerts in the selected window, including resolved ones."
          emptyMessage="No alerts in this window."
        />
      </section>

      <RulesEditor role={props.role} />
      <Silences role={props.role} />
      <Channels role={props.role} />
    </div>
  );
}

/** A KpiCard plus a stable React key, so the body can map a list without inventing keys. */
interface KeyedKpi {
  readonly key: string;
  readonly props: Parameters<typeof KpiCard>[0];
}

/**
 * The headline alert counts as KpiCards (observability-ui task 3.1). Every value is derived from the
 * SAME firing/history reads the regions below already use — nothing new is fetched and nothing is
 * fabricated. "Firing" counts currently-firing alerts, "Pending" the pending ones, "In window" the
 * full history page for the selected window. A firing count greater than zero is surfaced as a
 * `down` (bad) trend chip so the direction reads as attention-needed, never the healthy green; a
 * clean board stays neutral.
 */
function alertKpis(firing: readonly Alert[], history: readonly Alert[]): readonly KeyedKpi[] {
  const firingNow = firing.filter((a) => a.state === "firing").length;
  const pending = firing.filter((a) => a.state === "pending").length;
  const critical = firing.filter((a) => a.state === "firing" && a.severity === "critical").length;

  return [
    {
      key: "firing",
      props: {
        label: "Firing",
        value: firingNow,
        ...(firingNow > 0 ? { delta: `${firingNow}`, trend: "down" as const } : {}),
      },
    },
    { key: "pending", props: { label: "Pending", value: pending } },
    {
      key: "critical",
      props: {
        label: "Critical",
        value: critical,
        ...(critical > 0 ? { delta: `${critical}`, trend: "down" as const } : {}),
      },
    },
    { key: "history", props: { label: "In window", value: history.length } },
  ];
}

/**
 * Map the derived firing list onto the kit AlertsPanel's view model. Each row drills to this same
 * Alerts screen's history anchor (the detailed, ackable table is below). The panel shows its own
 * honest empty state when nothing is firing — no fabricated row.
 */
function firingPanelItems(firing: readonly Alert[]): readonly AlertsPanelItem[] {
  return firing.map((alert): AlertsPanelItem => ({
    id: alert.id,
    severity: alert.severity,
    title: alert.rule_name,
    target: severityLabel(alert.severity),
    at: alert.since,
    href: "#alr-history",
  }));
}
